import React, { useState } from 'react';
import {
  Box,
  Grid,
  TextField,
  Typography,
  MenuItem,
  Card,
  CardContent,
  Stack,
  Checkbox,
  FormControlLabel,
  Divider,
} from '@mui/material';

const CompanyDetails = ({ data, onChange, onSubmit }) => {
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [termsAccepted, setTermsAccepted] = useState(false);

  const handleOtpChange = (index) => (event) => {
    const value = event.target.value;
    if (value.length <= 1 && /^[0-9]*$/.test(value)) {
      const newOtp = [...otp];
      newOtp[index] = value;
      setOtp(newOtp);
      
      // Auto-focus next input
      if (value && index < 5) {
        const nextInput = document.querySelector(`input[name=otp-${index + 1}]`);
        if (nextInput) nextInput.focus();
      }

      // Update parent component with full OTP string
      onChange('companyDetails')({ otp: newOtp.join('') });
    }
  };

  const handleKeyDown = (index) => (event) => {
    if (event.key === 'Backspace' && !otp[index] && index > 0) {
      const prevInput = document.querySelector(`input[name=otp-${index - 1}]`);
      if (prevInput) prevInput.focus();
    }
  };

  const handleTermsChange = (event) => {
    setTermsAccepted(event.target.checked);
    onChange('companyDetails')({ termsAccepted: event.target.checked });
  };

  const handleFieldChange = (field) => (event) => {
    onChange('companyDetails')({ [field]: event.target.value });
  };

  return (
    <Box>
      <Card 
        variant="outlined" 
        sx={{ 
          mb: 4,
          borderRadius: 0,
          borderColor: 'divider'
        }}
      >
        <CardContent>
          <Typography 
            variant="subtitle2" 
            sx={{ 
              mb: 2,
              fontWeight: 500,
              color: 'text.secondary',
              fontSize: '0.875rem'
            }}
          >
            Enter your OTP and company details:
          </Typography>

          <Stack spacing={2}>
            {/* OTP Section */}
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <Typography 
                variant="subtitle2" 
                sx={{ 
                  mb: 0.5,
                  fontWeight: 500,
                  color: 'text.primary',
                  fontSize: '0.875rem'
                }}
              >
                OTP Code
              </Typography>
              <Typography 
                variant="caption" 
                sx={{ 
                  mb: 1,
                  color: 'text.secondary',
                  fontSize: '0.75rem'
                }}
              >
                Enter the 6-digit OTP received from ZATCA portal
              </Typography>
              <Box sx={{ display: 'flex', gap: 0.5 }}>
                {otp.map((digit, index) => (
                  <TextField
                    key={index}
                    name={`otp-${index}`}
                    value={digit}
                    onChange={handleOtpChange(index)}
                    onKeyDown={handleKeyDown(index)}
                    inputProps={{
                      maxLength: 1,
                      style: { 
                        textAlign: 'center',
                        fontSize: '1rem',
                        padding: '6px',
                        width: '36px',
                        height: '36px'
                      }
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 0,
                        '& fieldset': {
                          borderColor: 'divider'
                        },
                        '&:hover fieldset': {
                          borderColor: 'primary.main'
                        }
                      }
                    }}
                  />
                ))}
              </Box>
            </Box>

            {/* Company Details Section */}
            <Grid container spacing={1}>
              <Grid item xs={12} md={4}>
                <Typography 
                  variant="subtitle2" 
                  sx={{ 
                    mb: 0.5,
                    fontWeight: 500,
                    color: 'text.primary',
                    fontSize: '0.875rem'
                  }}
                >
                  Common Name
                </Typography>
                <TextField
                  fullWidth
                  value={data.companyDetails.commonName || ''}
                  onChange={handleFieldChange('commonName')}
                  placeholder="TST-886431145-399999999900003"
                  required
                  size="small"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 0,
                      '& fieldset': {
                        borderColor: 'divider'
                      }
                    }
                  }}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <Typography 
                  variant="subtitle2" 
                  sx={{ 
                    mb: 0.5,
                    fontWeight: 500,
                    color: 'text.primary',
                    fontSize: '0.875rem'
                  }}
                >
                  ESG Serial Number
                </Typography>
                <TextField
                  fullWidth
                  value={data.companyDetails.esgSerialNumber || ''}
                  onChange={handleFieldChange('esgSerialNumber')}
                  placeholder="1-TST|2-TST|3-ed22f1d8-e6a2-1118-9b58-d9a8f11e445f"
                  required
                  size="small"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 0,
                      '& fieldset': {
                        borderColor: 'divider'
                      }
                    }
                  }}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <Typography 
                  variant="subtitle2" 
                  sx={{ 
                    mb: 0.5,
                    fontWeight: 500,
                    color: 'text.primary',
                    fontSize: '0.875rem'
                  }}
                >
                  Organization Identifier
                </Typography>
                <TextField
                  fullWidth
                  value={data.companyDetails.organizationIdentifier || ''}
                  onChange={handleFieldChange('organizationIdentifier')}
                  placeholder="399999999900003"
                  required
                  size="small"
                  helperText="15-digit VAT registration number"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 0,
                      '& fieldset': {
                        borderColor: 'divider'
                      }
                    }
                  }}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <Typography 
                  variant="subtitle2" 
                  sx={{ 
                    mb: 0.5,
                    fontWeight: 500,
                    color: 'text.primary',
                    fontSize: '0.875rem'
                  }}
                >
                  Organization Unit Name
                </Typography>
                <TextField
                  fullWidth
                  value={data.companyDetails.organizationUnitName || ''}
                  onChange={handleFieldChange('organizationUnitName')}
                  placeholder="Riyadh Branch"
                  required
                  size="small"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 0,
                      '& fieldset': {
                        borderColor: 'divider'
                      }
                    }
                  }}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <Typography 
                  variant="subtitle2" 
                  sx={{ 
                    mb: 0.5,
                    fontWeight: 500,
                    color: 'text.primary',
                    fontSize: '0.875rem'
                  }}
                >
                  Organization Name
                </Typography>
                <TextField
                  fullWidth
                  value={data.companyDetails.organizationName || ''}
                  onChange={handleFieldChange('organizationName')}
                  placeholder="My Company LTD"
                  required
                  size="small"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 0,
                      '& fieldset': {
                        borderColor: 'divider'
                      }
                    }
                  }}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <Typography 
                  variant="subtitle2" 
                  sx={{ 
                    mb: 0.5,
                    fontWeight: 500,
                    color: 'text.primary',
                    fontSize: '0.875rem'
                  }}
                >
                  Country
                </Typography>
                <TextField
                  fullWidth
                  select
                  value={data.companyDetails.country || 'SA'}
                  onChange={handleFieldChange('country')}
                  required
                  size="small"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 0,
                      '& fieldset': {
                        borderColor: 'divider'
                      }
                    }
                  }}
                >
                  <MenuItem value="SA">Saudi Arabia</MenuItem>
                </TextField>
              </Grid>

              <Grid item xs={12} md={4}>
                <Typography 
                  variant="subtitle2" 
                  sx={{ 
                    mb: 0.5,
                    fontWeight: 500,
                    color: 'text.primary',
                    fontSize: '0.875rem'
                  }}
                >
                  Invoice Type
                </Typography>
                <TextField
                  fullWidth
                  select
                  value={data.companyDetails.invoiceType || '1100'}
                  onChange={handleFieldChange('invoiceType')}
                  required
                  size="small"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 0,
                      '& fieldset': {
                        borderColor: 'divider'
                      }
                    }
                  }}
                >
                  <MenuItem value="1000">1000 - Tax Invoice (Standard)</MenuItem>
                  <MenuItem value="1100">1100 - Simplified Tax Invoice</MenuItem>
                  <MenuItem value="0100">0100 - Tax Invoice for Third Party</MenuItem>
                  <MenuItem value="0110">0110 - Tax Invoice for Nominal</MenuItem>
                </TextField>
              </Grid>

              <Grid item xs={12} md={4}>
                <Typography 
                  variant="subtitle2" 
                  sx={{ 
                    mb: 0.5,
                    fontWeight: 500,
                    color: 'text.primary',
                    fontSize: '0.875rem'
                  }}
                >
                  Industry
                </Typography>
                <TextField
                  fullWidth
                  value={data.companyDetails.industry || ''}
                  onChange={handleFieldChange('industry')}
                  placeholder="Supply activities"
                  required
                  size="small"
                  helperText="Business category/industry type"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 0,
                      '& fieldset': {
                        borderColor: 'divider'
                      }
                    }
                  }}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <Typography 
                  variant="subtitle2" 
                  sx={{ 
                    mb: 0.5,
                    fontWeight: 500,
                    color: 'text.primary',
                    fontSize: '0.875rem'
                  }}
                >
                  Location
                </Typography>
                <TextField
                  fullWidth
                  value={data.companyDetails.location || ''}
                  onChange={handleFieldChange('location')}
                  placeholder="RRRD2929"
                  required
                  size="small"
                  helperText="Location address code"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 0,
                      '& fieldset': {
                        borderColor: 'divider'
                      }
                    }
                  }}
                />
              </Grid>

              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={termsAccepted}
                      onChange={handleTermsChange}
                      sx={{
                        '& .MuiSvgIcon-root': {
                          fontSize: 18,
                        },
                      }}
                    />
                  }
                  label={
                    <Typography variant="body2" color="text.secondary">
                      I agree to the terms and conditions
                    </Typography>
                  }
                />
              </Grid>

              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <Typography 
                  variant="body2" 
                  color="text.secondary"
                  sx={{ 
                    direction: 'rtl',
                    textAlign: 'right',
                    lineHeight: 1.8,
                    fontSize: '0.875rem'
                  }}
                >
                  شروط وأحكام نظام الفاتورة الإلكترونية:

                  - أنت مسؤول عن دقة المعلومات المقدمة
                  - يجب الالتزام بمتطلبات نظام الفواتير الإلكترونية
                  - نحن نلتزم بحماية بياناتك وفقاً لقوانين المملكة
                  - يمكنك التواصل مع الدعم الفني للحصول على المساعدة

                  بالضغط على "موافق"، فإنك توافق على هذه الشروط.
                </Typography>
              </Grid>
            </Grid>
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
};

export default CompanyDetails; 